import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, map, of } from 'rxjs';
import { ICON } from '../../constants/icons.constant';
import { UsersService } from '../../services/entity/users.service';
import { Icon } from '../../types/icon.type';
import { SearchConnector } from '../models/search-connector.model';
import { SearchResultItem } from '../models/search-result.model';
import { SearchScope, hasUnsupportedScope } from '../models/search-scope.model';
import { UserFilter, User } from '../../models/entity/user';

/** Maximum results a single section contributes — the palette shows sections, not pages. */
const MAX_RESULTS = 8;

/**
 * Reference connector: searches users through the existing `UsersService` and
 * demonstrates the connector-decides-the-action model — the primary action
 * navigates to the user, the secondary copies the email address.
 *
 * Register globally via `provideSearchConnectors(UsersSearchConnector)` or pass
 * per call through `openGlobalSearch(injector, { connectors: [...] })`.
 */
@Injectable({ providedIn: 'root' })
export class UsersSearchConnector implements SearchConnector {
  // -----------------------------------------------------------------------------------------------------
  // @ DEPENDENCIES
  // -----------------------------------------------------------------------------------------------------
  private readonly usersService = inject(UsersService);

  private readonly router = inject(Router);

  // -----------------------------------------------------------------------------------------------------
  // @ INSTANCE VARIABLES
  // -----------------------------------------------------------------------------------------------------

  readonly id = 'users';

  readonly label = 'Benutzer';

  readonly order = 10;

  readonly icon: Icon = ICON.userCircle;

  // -----------------------------------------------------------------------------------------------------
  // @ PUBLIC METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Searches users matching the query. Users cannot be filtered by any entity
   * scope — a scoped palette therefore gets no user results at all.
   *
   * @param query - The trimmed search term.
   * @param scope - The active scope.
   * @returns {Observable<SearchResultItem[]>} The matching users as palette results.
   */
  search(query: string, scope: SearchScope): Observable<SearchResultItem[]> {
    if (hasUnsupportedScope(scope, [])) {
      return of([]);
    }

    return this.usersService
      .getAll({
        filter: { filter: query } as UserFilter,
        pagination: { page: 1, pageSize: MAX_RESULTS },
      })
      .pipe(map((users) => users.map((user) => this.toResultItem(user))));
  }

  // -----------------------------------------------------------------------------------------------------
  // @ PRIVATE METHODS
  // -----------------------------------------------------------------------------------------------------

  /**
   * Maps a user to a palette result with its actions.
   *
   * @param user - The domain model.
   * @returns {SearchResultItem} The palette row.
   */
  private toResultItem(user: User): SearchResultItem {
    return {
      id: user.id,
      title: user.fullName,
      subtitle: user.email,
      icon: this.icon,
      payload: user,
      actions: [
        {
          id: 'open',
          label: 'Öffnen',
          icon: ICON.caretRight,
          execute: () => {
            this.router.navigate(['/users', user.id]);
          },
        },
        {
          id: 'copy-email',
          label: 'E-Mail kopieren',
          icon: ICON.copy,
          keepOpen: true,
          execute: async () => {
            await navigator.clipboard.writeText(user.email);
          },
        },
      ],
    };
  }
}
